import { useLayoutEffect } from 'react';

export default function useLandingMotion(rootRef) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const targets = [...root.querySelectorAll('[data-reveal]')];
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let observer;
    let frame = 0;

    const reveal = (element, immediate = false) => {
      if (immediate) element.classList.add('mf-reveal-immediate');
      element.classList.add('is-revealed');
      observer?.unobserve(element);
    };
    const configure = () => {
      observer?.disconnect();
      if (motion?.matches || typeof IntersectionObserver !== 'function') {
        root.classList.remove('mf-motion-ready');
        targets.forEach((element) => reveal(element, true));
        return;
      }
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' });
      root.classList.add('mf-motion-ready');
      targets.forEach((element) => {
        if (!element.classList.contains('is-revealed')) observer.observe(element);
      });
    };
    // Keyboard navigation must never land on a visually hidden control.
    const onFocus = (event) => {
      let element = event.target.closest('[data-reveal]');
      while (element && root.contains(element)) {
        reveal(element, true);
        element = element.parentElement?.closest('[data-reveal]');
      }
    };
    const updateProgress = () => {
      frame = 0;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? Math.max(0, Math.min(1, window.scrollY / scrollable)) : 0;
      root.style.setProperty('--mf-page-progress', progress.toFixed(4));
      root.classList.toggle('mf-has-scrolled', window.scrollY > 16);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(updateProgress);
    };

    configure();
    updateProgress();
    root.addEventListener('focusin', onFocus);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    motion?.addEventListener?.('change', configure);

    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      root.removeEventListener('focusin', onFocus);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      motion?.removeEventListener?.('change', configure);
      root.classList.remove('mf-motion-ready', 'mf-has-scrolled');
      root.style.removeProperty('--mf-page-progress');
      targets.forEach((element) => element.classList.remove('is-revealed', 'mf-reveal-immediate'));
    };
  }, [rootRef]);
}
